// @ts-check

/** @file Board initial-sync activity card wired to the flash-notifications activity API. */

import {FlashNotifications} from "flash-notifications"
import _ from "gettext-universal/build/src/translate.js"
import {useEffect, useRef} from "react"

import {createProjectSyncActivityTracker} from "./project-sync-activity-tracker.js"
import {useProjectSyncStatus} from "./project-sync-status.js"

/** @import {ActivityHandle} from "flash-notifications/build/flash-notifications.js" */
/** @import {ProjectSyncActivityTracker} from "./project-sync-activity-tracker.js" */

/**
 * Shows one initial-sync activity card per project and settles it when the
 * first sync cycle resolves; the standing status chip keeps showing state.
 * @param {{projectId: string | null}} args Screen project selection.
 * @returns {void} Nothing.
 */
export function useProjectSyncActivityIndicator({projectId}) {
  const status = useProjectSyncStatus()
  const trackerRef = useRef(/** @type {ProjectSyncActivityTracker | null} */ (null))
  const handleRef = useRef(/** @type {ActivityHandle | null} */ (null))

  if (trackerRef.current == null) {
    trackerRef.current = createProjectSyncActivityTracker()
  }

  useEffect(() => {
    const tracker = trackerRef.current
    if (!tracker) return
    const actions = tracker.transition({lastSuccessAt: status.lastSuccessAt, projectId, state: status.state})

    for (const action of actions) {
      if (action === "show") {
        handleRef.current = FlashNotifications.activity({message: _("Syncing…")})
      } else if (handleRef.current) {
        handleRef.current[action]()
        handleRef.current = null
      }
    }
  }, [projectId, status])
}
